import { type SocketConnectionParams, SocketService } from '@newo/realtime';
import { runtimeConfig } from '@/constants';
import {
	AgentCommand,
	type AgentRealtimeTransportEvents,
	type CommandHandler,
	CommandSchemas,
	type NotificationPayload,
	NotificationSchema,
	RawAgentCommand,
	type Unsubscribe
} from '@/types/agent-realtime';

type AgentTrainingRealtimeInitOptions = {
	finalEvent?: AgentCommand;
	onRealtimeConnected?: () => void;
};

type AgentTrainingRealtimeTransport = Pick<
	SocketService<AgentRealtimeTransportEvents>,
	'connect' | 'disconnect' | 'on'
>;

class AgentTrainingRealtime {
	private initialized = false;

	private handlers = new Map<AgentCommand, Set<(payload: unknown) => void>>();

	private notificationHandlers = new Set<
		(payload: NotificationPayload) => void
	>();

	private reconnectHandlers = new Set<() => void>();

	private onRealtimeConnected?: () => void;

	private lastConnectedSocket?: ReturnType<
		SocketService<AgentRealtimeTransportEvents>['connect']
	>;

	private lastConnectedSocketId?: string;

	private offCommand?: Unsubscribe;

	private offConnect?: Unsubscribe;

	private offNotification?: Unsubscribe;

	private offReconnect?: Unsubscribe;

	private offDiagnostics: Unsubscribe[] = [];

	private realtime?: AgentTrainingRealtimeTransport;

	private finalEvent?: AgentCommand;

	private getRealtime() {
		if (!this.realtime) {
			this.realtime = SocketService.create<AgentRealtimeTransportEvents>();
		}
		return this.realtime;
	}

	private resolveInitOptions(
		options?: AgentCommand | AgentTrainingRealtimeInitOptions
	): AgentTrainingRealtimeInitOptions {
		if (typeof options === 'string') {
			return { finalEvent: options };
		}

		return options ?? {};
	}

	private applyInitOptions(
		options?: AgentCommand | AgentTrainingRealtimeInitOptions
	) {
		if (!options) {
			return;
		}

		const initOptions = this.resolveInitOptions(options);

		if (Object.hasOwn(initOptions, 'finalEvent')) {
			this.finalEvent = initOptions.finalEvent;
		}

		if (Object.hasOwn(initOptions, 'onRealtimeConnected')) {
			this.updateConnectedHandler(initOptions.onRealtimeConnected);
		}
	}

	private updateConnectedHandler(onRealtimeConnected?: () => void) {
		if (this.onRealtimeConnected === onRealtimeConnected) {
			return;
		}

		this.onRealtimeConnected = onRealtimeConnected;
		this.lastConnectedSocket = undefined;
		this.lastConnectedSocketId = undefined;
	}

	private notifyRealtimeConnected(
		socket: ReturnType<SocketService<AgentRealtimeTransportEvents>['connect']>
	) {
		if (!socket.connected) {
			return;
		}

		if (
			this.lastConnectedSocket === socket &&
			this.lastConnectedSocketId === socket.id
		) {
			return;
		}

		this.lastConnectedSocket = socket;
		this.lastConnectedSocketId = socket.id;
		this.onRealtimeConnected?.();
	}

	private unbindTransportListeners() {
		this.offConnect?.();
		this.offCommand?.();
		this.offNotification?.();
		this.offReconnect?.();
		this.offConnect = undefined;
		this.offCommand = undefined;
		this.offNotification = undefined;
		this.offReconnect = undefined;
	}

	private unbindDiagnostics() {
		for (const off of this.offDiagnostics) {
			off();
		}
		this.offDiagnostics = [];
	}

	private formatSocketError(err: Error) {
		const socketError = err as Error & {
			context?: unknown;
			data?: unknown;
			description?: unknown;
		};

		return {
			message: err.message,
			name: err.name,
			stack: err.stack,
			data: socketError.data,
			description: socketError.description,
			context: socketError.context,
			error: err
		};
	}

	private bindDiagnostics(
		socket: ReturnType<SocketService<AgentRealtimeTransportEvents>['connect']>
	) {
		this.unbindDiagnostics();

		const manager = socket.io;
		const handleConnect = () => {
			console.info('[AgentTrainingRealtime] Socket connected', {
				id: socket.id,
				recovered: socket.recovered
			});
			this.notifyRealtimeConnected(socket);
		};
		const handleDisconnect = (reason: string, description?: unknown) => {
			console.warn('[AgentTrainingRealtime] Socket disconnected', {
				reason,
				description
			});
		};
		const handleConnectError = (err: Error) => {
			console.error(
				'[AgentTrainingRealtime] Socket connect_error',
				this.formatSocketError(err)
			);
		};
		const handleManagerOpen = () => {
			console.info('[AgentTrainingRealtime] Socket manager open');
		};
		const handleManagerClose = (reason: string, description?: unknown) => {
			console.warn('[AgentTrainingRealtime] Socket manager close', {
				reason,
				description
			});
		};
		const handleManagerError = (err: Error) => {
			console.error(
				'[AgentTrainingRealtime] Socket manager error',
				this.formatSocketError(err)
			);
		};
		const handleReconnectAttempt = (attempt: number) => {
			console.info('[AgentTrainingRealtime] Socket reconnect_attempt', {
				attempt
			});
		};
		const handleReconnect = (attempt: number) => {
			console.info('[AgentTrainingRealtime] Socket reconnected', {
				attempt,
				id: socket.id,
				recovered: socket.recovered
			});

			this.reconnectHandlers.forEach((cb) => {
				cb();
			});
		};
		const handleReconnectError = (err: Error) => {
			console.error(
				'[AgentTrainingRealtime] Socket reconnect_error',
				this.formatSocketError(err)
			);
		};
		const handleReconnectFailed = () => {
			console.error('[AgentTrainingRealtime] Socket reconnect_failed');
		};

		socket.on('connect', handleConnect);
		socket.on('disconnect', handleDisconnect);
		socket.on('connect_error', handleConnectError);
		manager.on('open', handleManagerOpen);
		manager.on('close', handleManagerClose);
		manager.on('error', handleManagerError);
		manager.on('reconnect_attempt', handleReconnectAttempt);
		manager.on('reconnect', handleReconnect);
		manager.on('reconnect_error', handleReconnectError);
		manager.on('reconnect_failed', handleReconnectFailed);

		this.offDiagnostics = [
			() => socket.off('connect', handleConnect),
			() => socket.off('disconnect', handleDisconnect),
			() => socket.off('connect_error', handleConnectError),
			() => manager.off('open', handleManagerOpen),
			() => manager.off('close', handleManagerClose),
			() => manager.off('error', handleManagerError),
			() => manager.off('reconnect_attempt', handleReconnectAttempt),
			() => manager.off('reconnect', handleReconnect),
			() => manager.off('reconnect_error', handleReconnectError),
			() => manager.off('reconnect_failed', handleReconnectFailed)
		];
	}

	private bindTransportListeners(realtime: AgentTrainingRealtimeTransport) {
		this.offConnect = realtime.on('connect', () => {
			if (!(realtime instanceof SocketService)) {
				this.onRealtimeConnected?.();
			}
		});
		this.offNotification = realtime.on('notification', (payload) => {
			const parsed = NotificationSchema.safeParse(payload);
			if (!parsed.success || this.notificationHandlers.size === 0) {
				return;
			}

			this.notificationHandlers.forEach((cb) => {
				cb(parsed.data);
			});
		});

		this.offCommand = realtime.on('command', (payload) => {
			this.handleCommand(payload);
		});
		this.offReconnect = realtime.on('reconnect', () => {
			this.reconnectHandlers.forEach((cb) => {
				cb();
			});
		});
	}

	init(
		params: Omit<SocketConnectionParams, 'endpoint'>,
		options?: AgentCommand | AgentTrainingRealtimeInitOptions
	) {
		const realtime = this.getRealtime();

		this.applyInitOptions(options);

		if (this.initialized) {
			if (!(realtime instanceof SocketService)) {
				return;
			}
			const currentSocket = realtime.getSocket();

			if (currentSocket) {
				this.notifyRealtimeConnected(currentSocket);
			} else {
				this.unbindTransportListeners();
				const socket = realtime.connect({
					...params,
					endpoint: runtimeConfig.chatApiEndpoint
				});
				this.bindTransportListeners(realtime);
				this.bindDiagnostics(socket);
				this.notifyRealtimeConnected(socket);
			}
			return;
		}

		this.bindTransportListeners(realtime);
		const socket = realtime.connect({
			...params,
			endpoint: runtimeConfig.chatApiEndpoint
		});

		if (realtime instanceof SocketService) {
			this.bindDiagnostics(socket);
			this.notifyRealtimeConnected(socket);
		}

		this.initialized = true;
	}

	private handleCommand(payload: unknown) {
		const envelope = RawAgentCommand.safeParse(payload);
		if (!envelope.success) {
			return;
		}

		const commandIdn = envelope.data.command_idn;
		const listeners = this.handlers.get(commandIdn);
		if (!listeners || listeners.size === 0) {
			return;
		}

		const schema = CommandSchemas[commandIdn];
		const parsed = schema.safeParse(envelope.data.arguments);

		if (!parsed.success) {
			return;
		}

		listeners.forEach((cb) => {
			cb(parsed.data);
		});

		if (this.finalEvent && commandIdn === this.finalEvent) {
			queueMicrotask(() => this.disconnect());
		}
	}

	disconnect() {
		this.handlers.clear();
		this.notificationHandlers.clear();
		this.reconnectHandlers.clear();
		this.onRealtimeConnected = undefined;
		this.lastConnectedSocket = undefined;
		this.lastConnectedSocketId = undefined;
		this.unbindDiagnostics();
		this.unbindTransportListeners();
		this.initialized = false;
		this.finalEvent = undefined;
		this.realtime?.disconnect();
		this.realtime = undefined;
	}

	private on<K extends AgentCommand>(
		commandIdn: K,
		cb: CommandHandler<K>
	): Unsubscribe {
		let set = this.handlers.get(commandIdn);

		if (!set) {
			set = new Set();
			this.handlers.set(commandIdn, set);
		}

		set.add(cb as (payload: unknown) => void);

		return () => {
			const current = this.handlers.get(commandIdn);
			if (!current) {
				return;
			}

			current.delete(cb as (payload: unknown) => void);

			if (current.size === 0) {
				this.handlers.delete(commandIdn);
			}
		};
	}

	onNotification(cb: (payload: NotificationPayload) => void): Unsubscribe {
		this.notificationHandlers.add(cb);

		return () => {
			this.notificationHandlers.delete(cb);
		};
	}

	onNotifyScrapingStep(cb: CommandHandler<AgentCommand.NotifyScrapingStep>) {
		return this.on(AgentCommand.NotifyScrapingStep, cb);
	}

	onNotifyScrapingFinished(
		cb: CommandHandler<AgentCommand.NotifyScrapingFinished>
	) {
		return this.on(AgentCommand.NotifyScrapingFinished, cb);
	}

	onOnboardingError(cb: CommandHandler<AgentCommand.OnboardingError>) {
		return this.on(AgentCommand.OnboardingError, cb);
	}

	onSetCreationSettings(cb: CommandHandler<AgentCommand.SetCreationSettings>) {
		return this.on(AgentCommand.SetCreationSettings, cb);
	}

	onShowCallNotification(
		cb: CommandHandler<AgentCommand.ShowCallNotification>
	) {
		return this.on(AgentCommand.ShowCallNotification, cb);
	}

	onOnboardingFinished(cb: CommandHandler<AgentCommand.OnboardingFinished>) {
		return this.on(AgentCommand.OnboardingFinished, cb);
	}

	onAgentInfo(cb: CommandHandler<AgentCommand.AgentInfo>) {
		return this.on(AgentCommand.AgentInfo, cb);
	}

	onFirstCustomerCallStarted(
		cb: CommandHandler<AgentCommand.FirstCustomerCallStarted>
	) {
		return this.on(AgentCommand.FirstCustomerCallStarted, cb);
	}

	onFirstCustomerCallFinished(
		cb: CommandHandler<AgentCommand.FirstCustomerCallFinished>
	) {
		return this.on(AgentCommand.FirstCustomerCallFinished, cb);
	}

	onCallCreationFinished(
		cb: CommandHandler<AgentCommand.CallCreationFinished>
	) {
		return this.on(AgentCommand.CallCreationFinished, cb);
	}

	onReconnect(cb: () => void) {
		this.reconnectHandlers.add(cb);

		return () => {
			this.reconnectHandlers.delete(cb);
		};
	}

	onConnect(cb: () => void) {
		return this.getRealtime().on('connect', cb);
	}
}

export const agentTrainingRealtime = new AgentTrainingRealtime();
